import React from 'react';

import { Box } from '@mui/material';

import { InfiniteQueryObserverBaseResult } from '@tanstack/react-query';
import { MRT_SortingState, MaterialReactTable, useMaterialReactTable } from 'material-react-table';

import { User, VenueListUsersFilter } from '../../../graphql/API';
import { PageView } from '../../../pages/Collabs/Collabs';
import { EmptyCollab } from '../../../pages/Collabs/components/EmptyCollab';
import { collabsDefaultFilters } from '../../../pages/Collabs/reducer';
import { UpgradePlanOverlay } from '../../UpgradePlanOverlay/UpgradePlanOverlay';
import columns from './Column';

type Props = {
  data: User[];
  onRowSelction: (values: string[]) => void;
  handleMarketChange?: () => void;
  handleSorting: (filters: Partial<VenueListUsersFilter> | undefined) => void;
  restrictFetching?: boolean;
  pageView?: PageView;
  selectedUsers?: string[];
  hasNoSubscriptionPlan?: boolean;
} & Pick<
  InfiniteQueryObserverBaseResult,
  'isFetching' | 'isLoading' | 'fetchNextPage' | 'hasNextPage'
>;

const getSelectedIds = (selectedRows: Record<number, boolean>): number[] => {
  // Convert the keys of the object to an array of numbers
  return Object.keys(selectedRows).map(Number);
};

export const CollabUsersTable: React.FC<Props> = ({
  data,
  isFetching,
  isLoading,
  fetchNextPage,
  hasNextPage,
  onRowSelction,
  handleMarketChange,
  handleSorting,
  restrictFetching = false,
  pageView,
  selectedUsers = [],
  hasNoSubscriptionPlan,
}) => {
  const elementRef = React.useRef<HTMLDivElement | null>(null);
  const [inScroll, setInScroll] = React.useState(false);

  const [sorting, setSorting] = React.useState<MRT_SortingState>([
    {
      id: collabsDefaultFilters?.sortBy || 'lastSeen',
      desc: collabsDefaultFilters?.sortDirection === 'desc',
    },
  ]);
  const [selectedRows, setSelectedRows] = React.useState<Record<number, boolean>>({});

  const prevSelectedRows = React.useRef(selectedRows);

  React.useEffect(() => {
    if (selectedUsers.length === 0) setSelectedRows({});
  }, [selectedUsers]);

  React.useEffect(() => {
    if (pageView === 'list') {
      if (JSON.stringify(prevSelectedRows.current) !== JSON.stringify(selectedRows)) {
        prevSelectedRows.current = selectedRows; // Update reference
        onRowSelction(getSelectedIds(selectedRows).map((id) => String(id)));
      }
    } else if (pageView === 'grid' && selectedUsers.length > 0) {
      setSelectedRows((prev) => {
        const newSelection = selectedUsers.reduce(
          (acc, id) => ({ ...acc, [Number(id)]: true }),
          {},
        );
        return JSON.stringify(prev) !== JSON.stringify(newSelection) ? newSelection : prev;
      });
    }
  }, [pageView, selectedRows, selectedUsers]);

  React.useEffect(() => {
    const [sort] = sorting;
    handleSorting({ sortBy: sort?.id, sortDirection: sort?.desc ? 'desc' : 'asc' });
  }, [sorting]);

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !isFetching &&
          hasNextPage &&
          !restrictFetching
        ) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetching, restrictFetching],
  );

  const handleScroll = React.useCallback(() => {
    const el = elementRef.current;
    console.log(el && el.scrollTop + el.clientHeight, el?.scrollHeight);
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight) {
      setInScroll(true);
    } else {
      setInScroll(false);
    }
  }, []);

  React.useEffect(() => {
    const el = elementRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const memoizedColumns = React.useMemo(
    () => columns(hasNoSubscriptionPlan || false),
    [hasNoSubscriptionPlan],
  );
  const memoizedData = React.useMemo(() => data, [data]);

  const table = useMaterialReactTable({
    enableRowSelection: true,
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setSelectedRows,
    muiTableHeadProps: { sx: { opacity: 1 } },
    muiTableHeadRowProps: {
      sx: { backgroundColor: '#FCFCFC', boxShadow: 'none' },
    },
    muiTableBodyRowProps: ({
      row: {
        original: { id },
      },
    }) => ({
      sx: {
        backgroundColor: selectedRows[Number(id)] ? '#F8F9FB' : 'inherit',
        cursor: 'pointer',
      },
    }),
    muiTableBodyCellProps: {
      sx: ({ custom }) => ({ borderColor: custom.colors.brandLightGrey }),
    },
    muiTableHeadCellProps: {
      sx: ({ custom }) => ({
        color: 'text.secondary',
        borderColor: custom.colors.brandLightGrey,
      }),
    },
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
    muiTableContainerProps: {
      ref: elementRef,
      sx: {
        height: 'calc(100vh - 195px)',
        maxHeight: 'calc(100vh - 195px)',
        backgroundColor: 'white',
      },
      onScroll: (event) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    muiFilterCheckboxProps: {
      color: 'secondary',
    },
    muiSelectCheckboxProps: {
      sx: {
        color: '#E0E0E0',
        '&.Mui-checked': {
          color: '#765BC6',
        },
      },
    },
    columns: memoizedColumns,
    data: memoizedData,
    rowVirtualizerOptions: { overscan: 10 },
    state: { isLoading: isLoading || isFetching, rowSelection: selectedRows, sorting },
    renderEmptyRowsFallback: () => <EmptyCollab handleMarketChange={handleMarketChange} />,
    getRowId: (row) => row.id,
  });

  return (
    <Box sx={{ p: 3, pt: 0 }}>
      <Box sx={{ borderRadius: '8px', border: '1px solid #EBEBEB' }}>
        <MaterialReactTable table={table} />
      </Box>

      {hasNoSubscriptionPlan && inScroll && <UpgradePlanOverlay />}
    </Box>
  );
};
